import * as Sentry from '@sentry/react'

/**
 * Sentry for the TV display.
 *
 * The TV is the one surface that runs unattended all day with nobody
 * looking at a console — until now its errors went to console.error and
 * vanished. The mobile app and API already report to Sentry; this closes
 * the gap so a gym TV crash shows up in the same place.
 *
 * DSN comes from VITE_SENTRY_DSN, baked at build time like the API URLs
 * (see apps/tv/Dockerfile — add it to [build.args] when a DSN exists).
 * No DSN (local dev, or simply not configured yet) → init is skipped
 * entirely and every helper below is a no-op. That's deliberate: dev
 * crashes are visible in the terminal already, and a half-configured
 * Sentry throwing its own errors on a gym TV would be worse than none.
 *
 * Sample rates: errors 100% (a fleet of ~2 screens per gym produces
 * almost no volume), traces 0 (we don't need performance spans from a
 * display; the API side already traces the interesting path).
 */
const DSN = (import.meta.env.VITE_SENTRY_DSN as string | undefined) ?? ''

export function initSentry(): void {
  if (!DSN) return
  Sentry.init({
    dsn: DSN,
    environment: import.meta.env.MODE,
    tracesSampleRate: 0,
  })
}

/** Attach the paired screen's identity so fleet errors are attributable. */
export function setSentryScreenContext(ctx: {
  screenName?: string | null
  roomId?: string | null
  tenantSlug?: string | null
}): void {
  if (!DSN) return
  Sentry.setTags({
    screenName: ctx.screenName ?? undefined,
    roomId: ctx.roomId ?? undefined,
    tenantSlug: ctx.tenantSlug ?? undefined,
  })
}

/** Report a fatal render crash (called from TvErrorBoundary pre-reload). */
export function reportFatal(error: unknown, componentStack?: string | null): void {
  if (!DSN) return
  Sentry.captureException(error, {
    tags: { surface: 'tv', fatal: 'true' },
    extra: { componentStack: componentStack ?? undefined },
  })
}

/**
 * Best-effort flush before the error boundary reloads the page — an
 * unflushed event dies with the document. 2s cap so a broken network
 * can't stall recovery.
 */
export async function flushSentry(): Promise<void> {
  if (!DSN) return
  try {
    await Sentry.flush(2000)
  } catch {
    /* flush failure must never block the reload */
  }
}
